import { useState, useEffect, useRef } from 'react';
import type { CallRecord } from '@/types/callRecord';
import { callStatusOptions, feedbackOptions } from '@/types/callRecord';

interface AddCallRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<CallRecord, 'id' | 'createdAt'>) => void;
  presetData?: {
    companyId: string;
    companyName: string;
    contactId?: string;
    contactName?: string;
    contactPhone?: string;
  } | null;
}

export default function AddCallRecordModal({ isOpen, onClose, onSubmit, presetData }: AddCallRecordModalProps) {
  const [callDate, setCallDate] = useState('');
  const [callTime, setCallTime] = useState('');
  const [status, setStatus] = useState<CallRecord['status']>('已接通');
  const [feedback, setFeedback] = useState<CallRecord['feedback']>('');
  const [notes, setNotes] = useState('');
  const [nextFollowUp, setNextFollowUp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const notesRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      setCallDate(now.toISOString().split('T')[0]);
      setCallTime(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
      setStatus('已接通');
      setFeedback('');
      setNotes('');
      setNextFollowUp('');
      setSubmitting(false);
      setTimeout(() => notesRef.current?.focus(), 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!presetData) return;
    setSubmitting(true);
    setTimeout(() => {
      onSubmit({
        companyId: presetData.companyId,
        companyName: presetData.companyName,
        contactId: presetData.contactId || '',
        contactName: presetData.contactName || '',
        contactPhone: presetData.contactPhone || '',
        callDate,
        callTime,
        status,
        feedback,
        notes,
        nextFollowUp: nextFollowUp || null,
      });
      setSubmitting(false);
      onClose();
    }, 400);
  };

  const canSubmit = presetData && callDate && callTime && status && notes.trim();

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-foreground-950/40" />
      <div
        className="relative bg-background-50 rounded-2xl w-full max-w-[520px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-background-50 border-b border-background-200/70 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-phone-line text-lg text-accent-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-foreground-900">新增通话记录</h2>
              {presetData && (
                <p className="text-sm text-foreground-500 truncate max-w-[300px]">
                  {presetData.companyName}{presetData.contactName ? ` · ${presetData.contactName}` : ''}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Contact info display */}
          {presetData && (
            <div className="bg-background-100 rounded-xl p-4 flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-secondary-200 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-secondary-700">
                  {(presetData.contactName || presetData.companyName).charAt(0)}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground-800">{presetData.contactName || presetData.companyName}</p>
                {presetData.contactPhone && (
                  <p className="text-xs text-foreground-500">{presetData.contactPhone}</p>
                )}
                <p className="text-xs text-foreground-400 truncate">{presetData.companyName}</p>
              </div>
            </div>
          )}

          {/* Call time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">通话日期</label>
              <input
                type="date"
                value={callDate}
                onChange={(e) => setCallDate(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">通话时间</label>
              <input
                type="time"
                value={callTime}
                onChange={(e) => setCallTime(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all cursor-pointer"
              />
            </div>
          </div>

          {/* Call status */}
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">通话状态</label>
            <div className="flex flex-wrap gap-1.5">
              {callStatusOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setStatus(opt as CallRecord['status'])}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    status === opt
                      ? 'bg-accent-100 border-accent-300 text-accent-700'
                      : 'bg-background-50 border-background-200 text-foreground-500 hover:border-background-300'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Feedback */}
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">联系反馈</label>
            <div className="flex flex-wrap gap-1.5">
              {feedbackOptions.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setFeedback(feedback === opt ? '' : (opt as CallRecord['feedback']))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    feedback === opt
                      ? 'bg-primary-100 border-primary-300 text-primary-700'
                      : 'bg-background-50 border-background-200 text-foreground-500 hover:border-background-300'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">
              沟通备注
              <span className="text-red-400 ml-0.5">*</span>
            </label>
            <textarea
              ref={notesRef}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              rows={4}
              placeholder="记录通话内容、客户反馈、关键信息等..."
              className="w-full px-3 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all resize-none"
            />
            <p className="text-xs text-foreground-400 mt-1 text-right">{notes.length}/500</p>
          </div>

          {/* Next follow-up */}
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">下次跟进日期（可选）</label>
            <div className="relative">
              <input
                type="date"
                value={nextFollowUp}
                onChange={(e) => setNextFollowUp(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all cursor-pointer"
              />
              {nextFollowUp && (
                <button
                  onClick={() => setNextFollowUp('')}
                  className="absolute right-10 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                >
                  <i className="ri-close-circle-line"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200/70 px-6 py-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2.5 border border-background-200 text-foreground-600 hover:bg-background-100 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="px-6 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                保存中...
              </span>
            ) : (
              '保存记录'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}